import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getUsersById } from 'auth/store/users/selectors';
import { getTagsById } from 'documents/store/tags/selectors';
import { getFoldersById } from 'documents/store/folders/selectors';
import { setActiveDocumentId } from 'documents/store/documents/actions';
import { getSpacesById } from 'spaces/store/selectors';
import Header from 'base/components/Header';
import MainTitle from 'base/components/MainTitle';
import { openModal } from '../../../modal';
import OfflineDocumentsList from '../../components/OfflineDocumentsList';
import { getOfflineDocumentIds, getOfflineDocuments } from '../../store/selectors';
import { uncacheDocumentOffline, addOfflineStorageErrorId } from '../../store/actions';
import OfflineFileStore from '../../util/OfflineFileStore';


const TextWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0 0 0;
`;

class ManageOfflineDocumentsPage extends React.PureComponent {
  componentDidMount() {
    this.verifyStorage();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.ids !== this.props.ids) {
      this.verifyStorage();
    }
  }

  verifyStorage = () => {
    // Sanity check: ensure offline file store contains all keys saved for offline mode
    const { ids, addOfflineStorageErrorId } = this.props; // eslint-disable-line
    if (ids.length === 0) {
      return;
    }
    OfflineFileStore.keys()
      .then((storageIds) =>
        ids.forEach((id) => {
          if (storageIds.length < 1 || !storageIds.includes(id.toString())) {
            addOfflineStorageErrorId(id);
          }
        }))
      .catch(() => ids.forEach((id) => addOfflineStorageErrorId(id)));
  };

  handleDocumentView = (document) => {
    this.props.setActiveDocumentId(document.id);
    this.props.openModal('DOCUMENT_VIEW');
  };

  handleUncacheDocument = (document) => {
    this.props.uncacheDocumentOffline(document);
  };

  render() {
    const {
      documents, usersById, tagsById, foldersById, spacesById,
    } = this.props;
    return (
      <>
        <Header style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <MainTitle>Manage offline documents</MainTitle>
          <TextWrapper>
            <p>
              You have selected the following documents for offline
              use
              {' '}
              <strong>on this device.</strong>
            </p>
            <p>
              If you use the Bucher Corporate Intranet without internet connection on this device,
              you will have access to these documents from your browser.
              <br />
              You can remove documents from the list below.
              {' '}
              <br />
            </p>
            <p>Please note that the offline mode is not supported by Internet Explorer.</p>
          </TextWrapper>
        </Header>
        <OfflineDocumentsList
          data={documents}
          usersById={usersById}
          tagsById={tagsById}
          foldersById={foldersById}
          spacesById={spacesById}
          onView={this.handleDocumentView}
          allowRemove
          onRemove={this.handleUncacheDocument}
          noDataText="You have no offline documents. In order to add more documents to the offline mode,
            click on the &#34;wifi off&#34; icon next to the file within the file browser."
        />
      </>
    );
  }
}
ManageOfflineDocumentsPage.propTypes = {
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  usersById: PropTypes.objectOf(PropTypes.any).isRequired,
  tagsById: PropTypes.objectOf(PropTypes.any).isRequired,
  foldersById: PropTypes.objectOf(PropTypes.any).isRequired,
  spacesById: PropTypes.objectOf(PropTypes.any).isRequired,
  setActiveDocumentId: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  uncacheDocumentOffline: PropTypes.func.isRequired,
  addOfflineStorageErrorId: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  ids: getOfflineDocumentIds(state),
  documents: getOfflineDocuments(state),
  usersById: getUsersById(state),
  tagsById: getTagsById(state),
  foldersById: getFoldersById(state),
  spacesById: getSpacesById(state),
});

const mapDispatchToProps = {
  setActiveDocumentId,
  openModal,
  uncacheDocumentOffline,
  addOfflineStorageErrorId,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ManageOfflineDocumentsPage);
